// src/components/Sidebar.js
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkflowList from './WorkflowList';
import Tour from './Tour';

// 预设机器配置
export const MACHINE_PRESETS = {
  printer3D: {
    'Prusa MK3S+': { width: 250, height: 250, depth: 210 },
    'Ender 3 V2': { width: 220, height: 220, depth: 250 },
    'Ultimaker S3': { width: 230, height: 190, depth: 200 },
    'Custom': { width: 10, height: 10, depth: 10 }
  },
  laserCutter: {
    'Glowforge Basic': { width: 279, height: 495 },
    'Epilog Zing 16': { width: 406, height: 305 },
    'Trotec Speedy 400': { width: 1016, height: 610 }, // 修改这里：从 'Trotec Speedy 300' 改为 'Trotec Speedy 400'，并更新尺寸
    'Custom': { width: 10, height: 10 }
  },
  wireBender: {
    'Manual Wire Bender': { maxLength: 1000, maxWireGauge: 2.5 },
    'Semi-Auto Wire Former': { maxLength: 2000, maxWireGauge: 5.0 },
    'C64 Wire Bender': { maxLength: 3000, maxWireGauge: 8.0 },
    'Custom': { maxLength: 1000, maxWireGauge: 2.5 }
  }
};

const Sidebar = () => {
  const navigate = useNavigate();
  const tourRef = useRef(null);

  // 约束设置状态
const [constraintsCollapsed, setConstraintsCollapsed] = useState(true);
const [machineSettings, setMachineSettings] = useState({
  printer3D: { selected: 'Prusa MK3S+', expanded: false },
  laserCutter: { selected: 'Trotec Speedy 400', expanded: false }, // 确保这里是 'Trotec Speedy 400'
  wireBender: { selected: 'C64 Wire Bender', expanded: false }
});

  // 材料状态
  const [materials, setMaterials] = useState([{ id: 1, name: 'Plywood', thickness: 3 }]);
  const [addingMaterial, setAddingMaterial] = useState(false);
  const [newMaterial, setNewMaterial] = useState({ name: '', thickness: '' });

  // 学习例子折叠
  const [learningExpanded, setLearningExpanded] = useState(false);

  useEffect(() => {
    tourRef.current = Tour();

    // 初始化全局约束信息
    updateGlobalConstraints(machineSettings, materials);

    const shouldStartTour = localStorage.getItem('cameleonTourShown') !== 'true';

    if (shouldStartTour) {
      const timer = setTimeout(() => {
        tourRef.current.startTour();
        localStorage.setItem('cameleonTourShown', 'true');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  // 监听约束变化，实时更新全局变量
  useEffect(() => {
    updateGlobalConstraints(machineSettings, materials);
  }, [machineSettings, materials]);

  const handleImportSTL = () => {
    navigate('/load-model');
  };

  const handleRestartTour = () => {
    if (tourRef.current) {
      tourRef.current.startTour();
    }
  };
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const getBaseThicknessFrom = (mats) => {
    if (!mats || !mats.length) return 3;
    const t = parseFloat(mats[0]?.thickness);
    return clamp(isNaN(t) ? 3 : t, 1, 12);
  };
  // 切换机器设置展开状态
  const toggleMachineExpanded = (machineType) => {
    setMachineSettings((prev) => ({
      ...prev,
      [machineType]: {
        ...prev[machineType],
        expanded: !prev[machineType].expanded
      }
    }));
  };

  // 更新全局约束信息
  const updateGlobalConstraints = (newMachineSettings, newMaterials) => {
    const baseT = getBaseThicknessFrom(newMaterials);
    window.labConstraints = {
      machineSettings: newMachineSettings,
      materials: newMaterials,
      materialBaseThickness: baseT,   // ← NEW
      lastUpdated: Date.now(),
    };
    window.dispatchEvent(
      new CustomEvent("labConstraints:updated", { detail: window.labConstraints })
    );
  };

  // 更新机器选择
  const updateMachineSelection = (machineType, selectedMachine) => {
    const newSettings = {
      ...machineSettings,
      [machineType]: {
        ...machineSettings[machineType],
        selected: selectedMachine,
        expanded: selectedMachine === 'Custom' // 如果选择Custom，自动展开输入框
      }
    };
    setMachineSettings(newSettings);
    updateGlobalConstraints(newSettings, materials);
  };

  // 添加材料
  const handleAddMaterial = () => {
    if (newMaterial.name && newMaterial.thickness) {
      const newMaterials = [
        ...materials,
        { id: Date.now(), name: newMaterial.name, thickness: parseFloat(newMaterial.thickness) }
      ];
      setMaterials(newMaterials);
      setNewMaterial({ name: '', thickness: '' });
      setAddingMaterial(false);
      updateGlobalConstraints(machineSettings, newMaterials);
    }
  };

  // 删除材料
  const removeMaterial = (id) => {
    const newMaterials = materials.filter((m) => m.id !== id);
    setMaterials(newMaterials);
    updateGlobalConstraints(machineSettings, newMaterials);
  };

  // 获取当前机器的尺寸设置
  const getMachineDimensions = (machineType) => {
    const selected = machineSettings[machineType].selected;
    return MACHINE_PRESETS[machineType][selected];
  };

  return (
    <div
      id="panel"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '300px',
        backgroundColor: 'white',
        borderRight: '1px solid #ddd',
        overflowY: 'auto',
        zIndex: 10
      }}
    >
      <div style={{ padding: '16px' }}>
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#3288e6'
          }}
        >
          <a
            id="cameleon-logo"
            href="https://cameleon-react-frontend-184431316116.us-central1.run.app/"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            CAMeleon
          </a>
        </h1>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}
        >
          <button
            id="importStlButton"
            onClick={handleImportSTL}
            style={{
              backgroundColor: '#3288e6',
              color: 'white',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
              border: 'none',
              fontSize: '14px',
              width: '120px',
              textAlign: 'center'
            }}
          >
            Import STL
          </button>

          <button
            id="restartTourButton"
            onClick={handleRestartTour}
            style={{
              backgroundColor: '#3288e6',
              color: 'white',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
              border: 'none',
              fontSize: '14px',
              width: '120px',
              textAlign: 'center'
            }}
          >
            Show Guide
          </button>
        </div>

        {/* 新增：约束设置区域 */}
        <div
          style={{
            marginBottom: '20px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
        >
          <div
            onClick={() => setConstraintsCollapsed(!constraintsCollapsed)}
            style={{
              padding: '12px 16px',
              cursor: 'pointer',
              borderBottom: constraintsCollapsed ? 'none' : '1px solid #e0e0e0',
              backgroundColor: constraintsCollapsed ? '#ffffff' : '#f8f9fa',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: constraintsCollapsed ? '8px' : '8px 8px 0 0',
              transition: 'all 0.2s ease',
              color: '#333'
            }}
          >
            Lab Equipment & Materials Available
            <span
              style={{
                fontSize: '12px',
                color: '#666',
                transition: 'transform 0.2s ease',
                transform: constraintsCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
              }}
            >
              ▼
            </span>
          </div>

          {!constraintsCollapsed && (
            <div style={{ padding: '16px' }}>
              {/* 3D打印机设置 */}
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '13px',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: '500',
                    color: '#555'
                  }}
                >
                  3D Printer
                  <select
                    value={machineSettings.printer3D.selected}
                    onChange={(e) => updateMachineSelection('printer3D', e.target.value)}
                    style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      padding: '4px 8px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      minWidth: '140px',
                      cursor: 'pointer'
                    }}
                  >
                    {Object.keys(MACHINE_PRESETS.printer3D).map((machine) => (
                      <option key={machine} value={machine}>
                        {machine}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => toggleMachineExpanded('printer3D')}
                    style={{
                      marginLeft: '6px',
                      background: 'none',
                      border: '1px solid #d0d0d0',
                      cursor: 'pointer',
                      fontSize: '10px',
                      padding: '4px 6px',
                      borderRadius: '3px',
                      color: '#666',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                  >
                    {machineSettings.printer3D.expanded ? '▲' : '▼'}
                  </button>
                </div>

                {machineSettings.printer3D.expanded && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      marginLeft: '0',
                      backgroundColor: '#f8f9fa',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef',
                      marginTop: '6px'
                    }}
                  >
                    <strong>Bed Size:</strong> {getMachineDimensions('printer3D').width} ×{' '}
                    {getMachineDimensions('printer3D').height} × {getMachineDimensions('printer3D').depth} mm
                  </div>
                )}
              </div>

              {/* 激光切割机设置 */}
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '13px',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: '500',
                    color: '#555'
                  }}
                >
                  Laser Cutter
                  <select
                    value={machineSettings.laserCutter.selected}
                    onChange={(e) => updateMachineSelection('laserCutter', e.target.value)}
                    style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      padding: '4px 8px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      minWidth: '140px',
                      cursor: 'pointer'
                    }}
                  >
                    {Object.keys(MACHINE_PRESETS.laserCutter).map((machine) => (
                      <option key={machine} value={machine}>
                        {machine}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => toggleMachineExpanded('laserCutter')}
                    style={{
                      marginLeft: '6px',
                      background: 'none',
                      border: '1px solid #d0d0d0',
                      cursor: 'pointer',
                      fontSize: '10px',
                      padding: '4px 6px',
                      borderRadius: '3px',
                      color: '#666',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                  >
                    {machineSettings.laserCutter.expanded ? '▲' : '▼'}
                  </button>
                </div>

                {machineSettings.laserCutter.expanded && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      marginLeft: '0',
                      backgroundColor: '#f8f9fa',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef',
                      marginTop: '6px'
                    }}
                  >
                    <strong>Bed Size:</strong> {getMachineDimensions('laserCutter').width} ×{' '}
                    {getMachineDimensions('laserCutter').height} mm
                  </div>
                )}
              </div>

              {/* 线材弯曲机设置 */}
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '13px',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: '500',
                    color: '#555'
                  }}
                >
                  Wire Bender
                  <select
                    value={machineSettings.wireBender.selected}
                    onChange={(e) => updateMachineSelection('wireBender', e.target.value)}
                    style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      padding: '4px 8px',
                      border: '1px solid #d0d0d0',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      minWidth: '140px',
                      cursor: 'pointer'
                    }}
                  >
                    {Object.keys(MACHINE_PRESETS.wireBender).map((machine) => (
                      <option key={machine} value={machine}>
                        {machine}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => toggleMachineExpanded('wireBender')}
                    style={{
                      marginLeft: '6px',
                      background: 'none',
                      border: '1px solid #d0d0d0',
                      cursor: 'pointer',
                      fontSize: '10px',
                      padding: '4px 6px',
                      borderRadius: '3px',
                      color: '#666',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
                  >
                    {machineSettings.wireBender.expanded ? '▲' : '▼'}
                  </button>
                </div>

                {machineSettings.wireBender.expanded && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      marginLeft: '0',
                      backgroundColor: '#f8f9fa',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef',
                      marginTop: '6px'
                    }}
                  >
                    <strong>Max Length:</strong> {getMachineDimensions('wireBender').maxLength}mm
                    <br />
                    <strong>Wire Gauge:</strong> {getMachineDimensions('wireBender').maxWireGauge}mm
                  </div>
                )}
              </div>

              {/* 材料设置 */}
              <div>
                <div
                  style={{
                    fontSize: '13px',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#555',
                    borderBottom: '1px solid #e9ecef',
                    paddingBottom: '4px'
                  }}
                >
                  Materials
                </div>

                {/* 材料列表 */}
                <div>
                  {materials.map((material) => (
                    <div
                      key={material.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '12px',
                        marginBottom: '6px',
                        padding: '8px 10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span style={{ color: '#444' }}>
                        {material.name} <strong>{material.thickness}mm</strong>
                      </span>
                      <button
                        onClick={() => removeMaterial(material.id)}
                        style={{
                          background: 'none',
                          border: '1px solid #dc3545',
                          color: '#dc3545',
                          cursor: 'pointer',
                          fontSize: '11px',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dc3545';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#dc3545';
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  {/* 添加材料表单 */}
                  {addingMaterial ? (
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '2px solid #3288e6',
                        marginTop: '6px'
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Material name (e.g., Plywood)"
                        value={newMaterial.name}
                        onChange={(e) => setNewMaterial((prev) => ({ ...prev, name: e.target.value }))}
                        style={{
                          width: '100%',
                          marginBottom: '6px',
                          padding: '6px 8px',
                          fontSize: '12px',
                          border: '1px solid #d0d0d0',
                          borderRadius: '4px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <input
                        type="number"
                        placeholder="Thickness (mm)"
                        value={newMaterial.thickness}
                        onChange={(e) => setNewMaterial((prev) => ({ ...prev, thickness: e.target.value }))}
                        style={{
                          width: '100%',
                          marginBottom: '8px',
                          padding: '6px 8px',
                          fontSize: '12px',
                          border: '1px solid #d0d0d0',
                          borderRadius: '4px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={handleAddMaterial}
                          style={{
                            fontSize: '12px',
                            padding: '6px 12px',
                            backgroundColor: '#3288e6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            flex: 1,
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = '#2876d4')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = '#3288e6')}
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setAddingMaterial(false);
                            setNewMaterial({ name: '', thickness: '' });
                          }}
                          style={{
                            fontSize: '12px',
                            padding: '6px 12px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            flex: 1,
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = '#5a6268')}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = '#6c757d')}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingMaterial(true)}
                      style={{
                        fontSize: '12px',
                        color: '#3288e6',
                        background: 'none',
                        border: '1px dashed #3288e6',
                        cursor: 'pointer',
                        padding: '8px',
                        width: '100%',
                        borderRadius: '4px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f0f7ff';
                        e.target.style.borderStyle = 'solid';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderStyle = 'dashed';
                      }}
                    >
                      + Add Material
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <WorkflowList />

        {/* 新添加的Examples区域 */}
        <div
          style={{
            marginTop: '20px',
            borderTop: '1px solid #e0e0e0',
            paddingTop: '15px'
          }}
        >
          <h2
            onClick={() => setLearningExpanded(!learningExpanded)}
            style={{
              fontSize: '1.2rem',
              fontWeight: 'bold',
              marginBottom: '0.8rem',
              color: '#28a745',
              backgroundColor: '#f0fff0',
              padding: '8px 12px',
              borderRadius: '6px',
              borderLeft: '4px solid #28a745',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            <span>Learning Examples</span>
            <span
              style={{
                fontSize: '14px',
                transform: learningExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}
            >
              ▼
            </span>
          </h2>

          {learningExpanded && (
            <>
              {/* Card 1 */}
              <div
                className="card"
                draggable="true"
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', 'Example1FloatingPart');
                  e.dataTransfer.effectAllowed = 'copy';
                  if (window.addToDraggedItems) {
                    window.addToDraggedItems('Example1FloatingPart');
                  }
                  e.currentTarget.classList.add('dragging');
                }}
                onDragEnd={(e) => {
                  e.currentTarget.classList.remove('dragging');
                }}
                style={{
                  cursor: 'move',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '15px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ padding: 0 }}>
                  <div style={{ overflow: 'hidden' }}>
                    <img
                      src="/images/example1_floating_part.jpg"
                      alt="Example 1: Floating Part Problem"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      loading="lazy"
                    />
                  </div>
                  <div style={{ padding: '12px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        color: '#444',
                        margin: '0 0 8px 0'
                      }}
                    >
                      Example 1: Floating Part Problem
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#e8f5e8',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Learning
                      </span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#f3e5f5',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Floating Parts
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div
                className="card"
                draggable="true"
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', 'Example2SupportProblem');
                  e.dataTransfer.effectAllowed = 'copy';
                  if (window.addToDraggedItems) {
                    window.addToDraggedItems('Example2SupportProblem');
                  }
                  e.currentTarget.classList.add('dragging');
                }}
                onDragEnd={(e) => {
                  e.currentTarget.classList.remove('dragging');
                }}
                style={{
                  cursor: 'move',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '15px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ padding: 0 }}>
                  <div style={{ overflow: 'hidden' }}>
                    <img
                      src="/images/example2_support_problem.jpg"
                      alt="Example 2: 3D Printing Support Problem"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      loading="lazy"
                    />
                  </div>
                  <div style={{ padding: '12px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        color: '#444',
                        margin: '0 0 8px 0'
                      }}
                    >
                      Example 2: 3D Printing Support Problem
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#e8f5e8',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Learning
                      </span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Overhangs
                      </span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#fff3e0',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Support Material
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div
                className="card"
                draggable="true"
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', 'Example3SizeLimitation');
                  e.dataTransfer.effectAllowed = 'copy';
                  if (window.addToDraggedItems) {
                    window.addToDraggedItems('Example3SizeLimitation');
                  }
                  e.currentTarget.classList.add('dragging');
                }}
                onDragEnd={(e) => {
                  e.currentTarget.classList.remove('dragging');
                }}
                style={{
                  cursor: 'move',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '15px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ padding: 0 }}>
                  <div style={{ overflow: 'hidden' }}>
                    <img
                      src="/images/example3_size_limitation.jpg"
                      alt="Example 3: Size Limitation Check"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      loading="lazy"
                    />
                  </div>
                  <div style={{ padding: '12px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        color: '#444',
                        margin: '0 0 8px 0'
                      }}
                    >
                      Example 3: Size Limitation Check
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#e8f5e8',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Learning
                      </span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#f3e5f5',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Machine Limits
                      </span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          backgroundColor: '#fff3e0',
                          borderRadius: '12px',
                          margin: '2px',
                          fontSize: '11px',
                          border: '1px solid #ddd'
                        }}
                      >
                        Size Matching
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {/* <-- 结束 Examples 容器 */}
      </div>
      {/* <-- 结束 padding 容器 */}
   </div>
 );
};

export default Sidebar;
